/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: Explore
 *| (C) Copyright IBM Corp. 2018, 2022
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */
import { types, getEnv, destroy } from 'mobx-state-tree';
import * as mobx from 'mobx';
import { fromPromise, FULFILLED, REJECTED } from 'mobx-utils';

import { fetchRecommendations } from 'util/FetchSmartsData';

import { FieldsModel } from './FieldsModel';
import RelationshipVisViewModel from './relationshipGraph/RelationshipVisViewModel';
import RelationshipVisLoader from './relationshipGraph/RelationshipVisLoader';
import FilterDockModel from 'features/filters/FilterDockModel';
import { isAlive } from 'mobx-state-tree';

const STARTING_POINTS = 'starting points';


export const Field = types
	.model('Field', {
		name: types.string,
		id: types.optional(types.string, '')
	});

export const StartingPointsViewModel = types
	.model('StartingPointsViewModel', {
		fields: FieldsModel,
		relationship: RelationshipVisViewModel,
		showModalVisTypePicker: types.optional(types.boolean, false)
	})
	.volatile(()=>({
		_isAlive: isAlive,
		exploreVM: null,
		relationshipSupported: null,	//Deprecated. Delete when newStartingPoints flag is removed
		recommendations: mobx.observable.map(),
		recommendationsOfStaticType: null,
		selectedInfluencers: [],
		filterDockModel: null,
		activeDataSourceName: null
	}))
	.extend((self) => {
		const disposeFunctions = [];

		const FIELD_ID_SEPARATOR = ';';
		function _fieldsToString(fieldIds, datasouceId) {
			const result = fieldIds.reduce((accumulator, fieldId, index) => {
				return `${accumulator}${index > 0 ? FIELD_ID_SEPARATOR : ''}${fieldId}`;
			}, '');
			return `${datasouceId}_${result}`; //Prefix fields string with datasource ID
		}

		//Deprecated. Delete this function when the newStartingPoints flag is removed.
		function initializeRelationshipSupported() {
			const relationshipSupportedFunctions = {};
			const promise = new Promise((resolve, reject) => {
				relationshipSupportedFunctions.resolve = resolve;
				relationshipSupportedFunctions.reject = reject;
			});
			const relSupported = fromPromise( promise );
			self.setRelationshipSupported(relSupported);
			//On first response from smarts service, determine whether it's functioning correctly
			const disposeRelationshipSupportedResolver = mobx.autorun(() => {
				if(shouldExecuteToGetFields()){
					const { interesting } = self.fields.getFields();

					if(interesting.state === REJECTED) {
					//If fields cannot be loaded, cannot render relationship
						relationshipSupportedFunctions.reject(interesting.error);
					} else if(interesting.state === FULFILLED) {
						const targetModel =  self.relationship.getTargetModel();
						const relationshipPromise = targetModel && targetModel.data;
						if(relationshipPromise && relationshipPromise.state === REJECTED) {
						//In general, we assume that errors which occur when rendering the
						//relationship diagram are *potentially* recoverable by selecting a
						//new field, so we can resolve the relationship supported promise.
						//The exception is if there is a missing smarts module, however this
						//case should fail the fields promise, preventing relationship
						//promise from ever finishing.
							relationshipSupportedFunctions.resolve();
						} else if(relationshipPromise && relationshipPromise.state === FULFILLED) {
							relationshipSupportedFunctions.resolve();
						}
					}
				}
			});
			//Whether relationship is supported is determined upon first response from smarts server.
			//Once relationship support is determined, we no longer need the autorun logic.
			self.relationshipSupported.then(	//Node 8 doesn't support Promise.finally
				() => disposeRelationshipSupportedResolver(),
				() => disposeRelationshipSupportedResolver()
			);
		}

		//We only send requests to get interesting fields if the current display card is starting points
		function shouldExecuteToGetFields(){
			return  self._isAlive(self.exploreVM) && self.exploreVM.activeDataSourceId && self.exploreVM.displayedCard === STARTING_POINTS;
		}

		function _setActiveDataSourceName(dataSourceId) {
			getEnv(self).dashboardApi.getFeature('DataSources')
				.getDataSource(dataSourceId).getLocalizedName()
				.then(sourceName => {
					if (sourceName) {
						self.setActiveDataSourceName(sourceName);
					}
				});
		}

		function _switchFilterDockDatasource(activeDataSourceId) {
			if (self.exploreVM.isStartingPointsDisplayed()) {
				self.filterDockModel.switchDatasource(activeDataSourceId);
				if (self.filterDockModel.getCurrentExtensionScope() != STARTING_POINTS) {
					self.exploreVM.notifyTabChanged(STARTING_POINTS);
				}
			}
		}

		return {
			views: {
				getSelectedInfluencers() {
					if(self.selectedInfluencers && self.selectedInfluencers.length) {
						return self.selectedInfluencers;
					}

					if(!self.isNewStartingPointsFeatureEnabled()) {
						return null;
					}

					if(self.relationship) {
						const field = self.fields.getFocusField();
						if(field) {
							return [{ id: field.id, label: field.name }];
						}
					}

					return null;
				},

				getRecommendations(fieldIds) {
					if (!fieldIds || fieldIds.length === 0) {
						return null;
					}
					return self.recommendations.get(_fieldsToString(fieldIds, self.exploreVM.activeDataSourceId)) || self._fetchRecommendedVisualizations(fieldIds);
				},
				isRecommendationOfStaticType(recommendationKey) {
					return self.recommendationsOfStaticType.indexOf(recommendationKey) > -1;
				},
				_fetchRecommendedVisualizations(fieldIds) {
					// Clone the fieldIds to protect it from being modified by the fetch
					const recommendations = fromPromise(fetchRecommendations(self.exploreVM, fieldIds.slice()));
					self.recommendations.set(_fieldsToString(fieldIds, self.exploreVM.activeDataSourceId), recommendations);
					return recommendations;
				},
				loadVisForSelectedInfluencers() {
					return self._fetchRecommendedVisualizations(self.getSelectedInfluencers());
				},
				isNewStartingPointsFeatureEnabled() {
					return !getEnv(self).isFeatureEnabled('newStartingPoints', 'false');
				},
				shouldUseDisambiguatedPaths() {
					const env = getEnv(self);
					return env && env.isFeatureEnabled && !env.isFeatureEnabled('useDisambiguatedPaths', 'false');
				},
				showFilterDock() {
					return !!self.fields.getFocusField();
				},
				getActiveDataSourceName() {
					return self.activeDataSourceName;
				}
			},
			actions: {
				afterCreate() {
					self._isAlive = getEnv(self).isAlive || self._isAlive;
					self.exploreVM = getEnv(self).exploreVM;
					self.dashboardApi = getEnv(self).dashboardApi;
					self.recommendationsOfStaticType = [];
					self.relationship.setExploreViewId(getEnv(self).exploreViewId);
					self.relationship.setSmartsRecommenderWrapper(self.exploreVM.getSmartsRecommenderWrapper());
					//StartingPointViewModel holds one VisLoader instance only
					const visLoader = new RelationshipVisLoader({
						dashboardApi: self.dashboardApi,
						startingPointsVM: self
					});
					self.relationship.setVisLoader(visLoader);
					const isFilterStrengthFeatureEnabled = getEnv(self).isFeatureEnabled('filterRelationshipStrength');
					self.relationship.setIsFilterStrengthFeatureEnabled(isFilterStrengthFeatureEnabled);
					self.filterDockModel = FilterDockModel.create({
						scopeId: STARTING_POINTS
					}, {
						dashboardApi: self.dashboardApi,
						exploreVM: self.exploreVM,
						logger: getEnv(self).logger
					});
					self.filterDockModel.registerFilterCountPayloadExtension((object) => {
						return {
							...object,
							sourceId: self.exploreVM.activeDataSourceId,
							_strictMatch: true
						};

					});
					//On set/change datasource, initialize new relationshipSupported promise
					disposeFunctions.push(
						mobx.reaction(
							() => self.exploreVM.activeDataSourceId,
							activeDataSourceId => {
								if(activeDataSourceId) {
									_setActiveDataSourceName(activeDataSourceId);
									self.setSelectedInfluencers();
									if(!self.isNewStartingPointsFeatureEnabled()) {
										initializeRelationshipSupported();
									}
									self.filterDockModel.initializeFilterDock(activeDataSourceId)
										.then(() => _switchFilterDockDatasource(activeDataSourceId));
								}
							}
						)
					);

					disposeFunctions.push(
						mobx.reaction( () => {
							if(shouldExecuteToGetFields()){
								const fields = self.fields.getFields();
								let fieldsReady = false;
								if(self.isNewStartingPointsFeatureEnabled()) {
									if (fields.all.state === FULFILLED) {
										fieldsReady = true;
									}
								} else {
									fieldsReady = fields.interesting.state === FULFILLED;
								}
								if(fieldsReady) {
									const field = self.fields.getFocusField();
									return field;
								}
							}
						},
						(field)=>{
							if(self.relationship) {
								if(field) {
									//Initialize relationship chart
									self.relationship.setTargetIds([field.id], [{ label: field.name }]);
									self.relationship.setDataSourceId(self.exploreVM.activeDataSourceId);
									self.setSelectedInfluencers(self.selectedInfluencers);

								} else {
									//Clear relationship chart
									self.relationship.setTargetIds([]);
									if (self.exploreVM.activeDataSourceId) {
										self.relationship.setDataSourceId(self.exploreVM.activeDataSourceId);
									} else {
										self.relationship.setDataSourceId(null);
									}
								}
							}
						}
							// No other state is possible here - see relationshipSupported promise
						)

					);
				},

				/* TO-DO this function is actually never called
				 * when we do call it we should destroy filterDockModel
				 */
				beforeDestroy() {
					disposeFunctions.forEach(dispose => dispose());
					self.filterDockModel && destroy(self.filterDockModel);
				},
				setModelChecker( isAlive ) {
					self._isAlive = isAlive;
				},
				setRelationshipSupported(relationshipSupported) {
					self.relationshipSupported = relationshipSupported;
				},
				setShowModalVisTypePicker(show) {
					self.showModalVisTypePicker = show;
				},
				setSelectedInfluencers(fields = []) {
					self.selectedInfluencers = fields;
					self.recommendationsOfStaticType = [];
				},
				addRecommendationsOfStaticType(recommendationKey) {
					self.recommendationsOfStaticType.push(recommendationKey);
				},
				reset() {
					self.fields.resetData();
					const field = self.fields.getFocusField();
					self.relationship.reset();
					if(field) {
						self.relationship.setTargetIds([field.id], [{ label: field.name }]);
					}
					self.relationship.setDataSourceId(self.exploreVM.activeDataSourceId);
				},
				refreshStartingPoints(){
					//we are forcing a refresh of the starting points
					self.fields.clearFields();
					self.setSelectedInfluencers();
					initializeRelationshipSupported();
				},
				setActiveDataSourceName(dataSourceName) {
					self.activeDataSourceName = dataSourceName;
				}
			}
		};
	});



// WEBPACK FOOTER //
// ./src/js/components/startingPoints/StartingPointsViewModel.js